import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { listDirectory } from "@/lib/hr-extra";
import { DEPARTMENT_LABELS, type Department, useAuth } from "@/lib/auth-context";
import { EmployeeProfileModal } from "@/components/hr/EmployeeProfileModal";
import { StatusPill } from "@/components/ui/status-pill";
import { createUser } from "@/lib/rpc/admin.functions";
import { listOpenOffboardings } from "@/lib/offboarding";
import { OffboardingBadge } from "@/components/hr/InitiateOffboardingDialog";

export const Route = createFileRoute("/_app/_hr/hr/directory")({
  component: DirectoryPage,
  head: () => ({ meta: [{ title: "All Employees — HR" }] }),
});


const Dash = () => <span style={{ color: "#9CA3AF" }}>—</span>;

function safeFormat(value: string | null, fmt: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return format(d, fmt);
}

function formatTenure(value: string | null): string | null {
  if (!value) return null;
  const joined = new Date(value);
  if (isNaN(joined.getTime())) return null;
  const now = new Date();
  const totalMonths =
    (now.getFullYear() - joined.getFullYear()) * 12 +
    (now.getMonth() - joined.getMonth());
  if (totalMonths < 1) return "Just joined";
  if (totalMonths < 12) return `${totalMonths} month${totalMonths > 1 ? "s" : ""}`;
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  if (months === 0) return `${years} year${years > 1 ? "s" : ""}`;
  return `${years} year${years > 1 ? "s" : ""} ${months} month${months > 1 ? "s" : ""}`;
}

function DirectoryPage() {
  const { role, profile } = useAuth();
  const canCreate = role === "admin" || profile?.department === "hr";
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [empTypeFilter, setEmpTypeFilter] = useState<string>("all");
  const [statusView, setStatusView] = useState<"active" | "inactive" | "all" | "offboarding">("active");
  const [openId, setOpenId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: rows = [] } = useQuery({ queryKey: ["hr-directory"], queryFn: listDirectory });
  const { data: offboardingMap = {} } = useQuery({
    queryKey: ["offboarding-open-map", rows.map((r) => r.id).join(",")],
    queryFn: () => listOpenOffboardings(rows.map((r) => r.id)),
    enabled: rows.length > 0 && (role === "admin" || profile?.department === "hr"),
  });


  const counts = useMemo(() => ({
    active: rows.filter((r) => r.status === "active").length,
    inactive: rows.filter((r) => r.status === "inactive").length,
    offboarding: Object.keys(offboardingMap).length,
    all: rows.length,
  }), [rows, offboardingMap]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    return rows.filter((r) => {
      if (statusView === "active" && r.status !== "active") return false;
      if (statusView === "inactive" && r.status !== "inactive") return false;
      if (statusView === "offboarding" && !offboardingMap[r.id]) return false;
      if (deptFilter !== "all" && r.department !== deptFilter) return false;
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (empTypeFilter !== "all" && empTypeFilter !== "permanent") return false;
      if (!s) return true;
      return (
        r.full_name.toLowerCase().includes(s) ||
        r.email.toLowerCase().includes(s) ||
        (r.work_email ?? "").toLowerCase().includes(s) ||
        (r.employee_id ?? "").toLowerCase().includes(s) ||
        (r.gender ?? "").toLowerCase().includes(s) ||
        (r.phone ?? "").toLowerCase().includes(s) ||
        r.department.toLowerCase().includes(s)
      );
    });
  }, [rows, search, deptFilter, statusFilter, empTypeFilter, statusView, offboardingMap]);

  const headStyle = "uppercase text-[11px] font-semibold text-muted-foreground";

  const pillClass = (active: boolean) =>
    `h-8 px-3 text-xs rounded-md border transition-colors ${
      active ? "bg-foreground text-background border-foreground" : "bg-background hover:bg-muted"
    }`;

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">All Employees</h1>
          <p className="text-xs text-muted-foreground">Record of every employee — active and inactive. Employee IDs are entered manually.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, ID, email, dept..."
            className="h-9 w-72 text-xs"
          />
          {canCreate && (
            <Button size="sm" className="h-9" onClick={() => setCreateOpen(true)}>
              + Add Employee
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5">
        <button className={pillClass(statusView === "active")} onClick={() => setStatusView("active")}>
          Active ({counts.active})
        </button>
        <button className={pillClass(statusView === "inactive")} onClick={() => setStatusView("inactive")}>
          Inactive ({counts.inactive})
        </button>
        <button className={pillClass(statusView === "offboarding")} onClick={() => setStatusView("offboarding")}>
          Offboarding in progress ({counts.offboarding})
        </button>
        <button className={pillClass(statusView === "all")} onClick={() => setStatusView("all")}>
          All ({counts.all})
        </button>
      </div>



      <div className="flex flex-wrap items-center gap-2">
        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger className="h-8 w-44 text-xs"><SelectValue placeholder="Department" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {(Object.keys(DEPARTMENT_LABELS) as Department[]).map((d) => (
              <SelectItem key={d} value={d}>{DEPARTMENT_LABELS[d]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-36 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
        <Select value={empTypeFilter} onValueChange={setEmpTypeFilter}>
          <SelectTrigger className="h-8 w-40 text-xs"><SelectValue placeholder="Employment Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="permanent">Permanent</SelectItem>
            <SelectItem value="intern">Intern</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border bg-card overflow-x-auto">
        <Table className="min-w-[1190px]">
          <TableHeader>
            <TableRow>
              <TableHead className={headStyle} style={{ width: 100 }}>Employee ID</TableHead>
              <TableHead className={headStyle} style={{ width: 130 }}>Name</TableHead>
              <TableHead className={headStyle} style={{ width: 80 }}>Gender</TableHead>
              <TableHead className={headStyle} style={{ width: 200 }}>Work Email</TableHead>
              <TableHead className={headStyle} style={{ width: 130 }}>Phone</TableHead>
              <TableHead className={headStyle} style={{ width: 130 }}>Department</TableHead>
              <TableHead className={headStyle} style={{ width: 100 }}>Joined Since</TableHead>
              <TableHead className={headStyle} style={{ width: 110 }}>DOB</TableHead>
              <TableHead className={headStyle} style={{ width: 90 }}>Status</TableHead>
              <TableHead className={`${headStyle} text-right`} style={{ width: 120 }}>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((r, idx) => {
              const workEmail = r.work_email && r.work_email.trim() ? r.work_email : r.email;
              const joined = formatTenure(r.joining_date);
              const dob = safeFormat(r.date_of_birth, "dd MMM yyyy");
              const rowBg = idx % 2 === 1 ? "#FAFAFA" : "transparent";
              return (
                <TableRow key={r.id} style={{ minHeight: 52, backgroundColor: rowBg }}>
                  <TableCell className="font-mono text-xs" style={{ height: 52 }}>
                    {r.employee_id ?? <Dash />}
                  </TableCell>
                  <TableCell className="text-xs font-bold">{r.full_name}</TableCell>
                  <TableCell className="text-xs">{r.gender ?? <Dash />}</TableCell>
                  <TableCell className="text-xs">
                    <span title={workEmail} className="block max-w-[190px] truncate">{workEmail}</span>
                  </TableCell>
                  <TableCell className="text-xs">{r.phone ?? <Dash />}</TableCell>
                  <TableCell className="text-xs">{DEPARTMENT_LABELS[r.department as Department] ?? r.department}</TableCell>
                  <TableCell className="text-xs">{joined ?? <Dash />}</TableCell>
                  <TableCell className="text-xs">{dob ?? <Dash />}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <StatusPill status={r.status} label={r.status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())} />
                      {offboardingMap[r.id] && (
                        <OffboardingBadge
                          status={offboardingMap[r.id].status as "notice_period" | "finalizing"}
                          lastWorkingDay={offboardingMap[r.id].last_working_day}
                        />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <button
                      onClick={() => setOpenId(r.id)}
                      className="text-xs text-primary hover:underline"
                    >
                      Open Profile →
                    </button>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={10} className="text-center text-xs text-muted-foreground">No employees found</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <EmployeeProfileModal profileId={openId} open={!!openId} onOpenChange={(v) => !v && setOpenId(null)} />
      {canCreate && (
        <AddEmployeeDialog open={createOpen} onOpenChange={setCreateOpen} />
      )}
    </div>
  );
}

const addSchema = z.object({
  full_name: z.string().min(1, "Required"),
  email: z.string().email("Valid work email required"),
  password: z.string().min(6, "At least 6 characters"),
  employee_id: z.string().regex(/^\d{4,}$/, "Numeric, at least 4 digits"),
  department: z.string().min(1),
  phone: z.string().optional().or(z.literal("")),
  gender: z.string().optional().or(z.literal("")),
  date_of_birth: z.string().optional().or(z.literal("")),
  joining_date: z.string().optional().or(z.literal("")),
  monthly_target: z.coerce.number().int().min(0).max(1000).optional(),
});
type AddValues = z.infer<typeof addSchema>;

function AddEmployeeDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const form = useForm<AddValues>({
    resolver: zodResolver(addSchema),
    defaultValues: {
      full_name: "",
      email: "",
      password: "",
      employee_id: "",
      department: "editor",
      phone: "",
      gender: "",
      date_of_birth: "",
      joining_date: "",
      monthly_target: undefined,
    },
  });

  const mut = useMutation({
    mutationFn: (v: AddValues) =>
      createUser({
        data: {
          full_name: v.full_name,
          email: v.email,
          password: v.password,
          employee_id: v.employee_id,
          department: v.department as Department,
          monthly_target: v.monthly_target,
          phone: v.phone || undefined,
          gender: v.gender || undefined,
          date_of_birth: v.date_of_birth || undefined,
          joining_date: v.joining_date || undefined,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr-directory"] });
      qc.invalidateQueries({ queryKey: ["admin"] });
      toast.success("Employee created");
      form.reset();
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Employee</DialogTitle>
          <DialogDescription>
            Creates a plain user account (no elevated permissions). Only an admin can promote or grant access later from Settings → Users.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit((v) => mut.mutate(v))} className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Full name" error={form.formState.errors.full_name?.message}>
              <Input {...form.register("full_name")} />
            </Field>
            <Field label="Work email" error={form.formState.errors.email?.message}>
              <Input type="email" {...form.register("email")} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Employee ID" error={form.formState.errors.employee_id?.message}>
              <Input inputMode="numeric" placeholder="e.g. 1001" {...form.register("employee_id")} />
            </Field>
            <Field label="Temporary password" error={form.formState.errors.password?.message}>
              <Input type="text" {...form.register("password")} placeholder="Share with the user" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Department">
              <Select
                value={form.watch("department")}
                onValueChange={(v) => form.setValue("department", v)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(DEPARTMENT_LABELS) as Department[]).map((d) => (
                    <SelectItem key={d} value={d}>{DEPARTMENT_LABELS[d]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Phone">
              <Input {...form.register("phone")} placeholder="Optional" />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Gender">
              <Select
                value={form.watch("gender") || ""}
                onValueChange={(v) => form.setValue("gender", v)}
              >
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Date of birth">
              <Input type="date" {...form.register("date_of_birth")} />
            </Field>
            <Field label="Joining date">
              <Input type="date" {...form.register("joining_date")} />
            </Field>
          </div>
          <Field label="Monthly target (optional)">
            <Input type="number" min={0} {...form.register("monthly_target")} placeholder="Default per department" />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={mut.isPending}>
              {mut.isPending ? "Creating…" : "Create employee"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1">
      <Label className="text-[11px]">{label}</Label>
      {children}
      {error && <span className="text-[11px] text-destructive">{error}</span>}
    </div>
  );
}

